import { toast as sonnerToast, ExternalToast } from "sonner";

/**
 * Custom Toast Wrapper for Animated Progress Bar
 *
 * Wraps Sonner's toast API to inject duration metadata into the DOM,
 * enabling CSS-based progress bar animations that match toast lifetimes.
 *
 * @example
 * import { toast } from "utils/toast";
 * toast.success("Success!", { duration: 5000 });
 */

const DEFAULT_DURATION = 4000;
const FALLBACK_DELAY = 100;

// FIFO queue for pending toast durations
const durationQueue: number[] = [];

/**
 * Applies duration metadata to a toast element for CSS animation sync
 */
const applyDuration = (element: HTMLElement, duration: number): void => {
  element.setAttribute("data-toast-duration", String(duration));
  element.style.setProperty("--toast-duration", String(duration));
};

/**
 * Watches for new toast elements and applies queued durations
 */
const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (
        node instanceof HTMLElement &&
        node.hasAttribute("data-sonner-toast")
      ) {
        const duration = durationQueue.shift();
        if (duration !== undefined) {
          applyDuration(node, duration);
        }
      }
    }
  }
});

// Initialize observer when DOM is ready
if (typeof document !== "undefined") {
  const startObserver = () =>
    observer.observe(document.body, { childList: true, subtree: true });
  document.readyState === "loading"
    ? document.addEventListener("DOMContentLoaded", startObserver)
    : startObserver();
}

/**
 * Wraps a toast function to inject duration tracking
 */
const createToastWrapper = (
  toastFn: (
    message: string | React.ReactNode,
    data?: ExternalToast
  ) => string | number
) => {
  return (message: string | React.ReactNode, data?: ExternalToast) => {
    const duration = data?.duration ?? DEFAULT_DURATION;

    // Queue duration before creating toast to ensure FIFO ordering
    if (duration !== Infinity) {
      durationQueue.push(duration);
    }

    const toastId = toastFn(message, data);

    // Fallback: manually apply duration if observer misses the toast
    if (duration !== Infinity) {
      setTimeout(() => {
        const queueIndex = durationQueue.indexOf(duration);
        if (queueIndex !== -1) {
          durationQueue.splice(queueIndex, 1);
          const toasts = document.querySelectorAll<HTMLElement>(
            "[data-sonner-toast]"
          );
          const lastToast = toasts[toasts.length - 1];
          if (lastToast && !lastToast.hasAttribute("data-toast-duration")) {
            applyDuration(lastToast, duration);
          }
        }
      }, FALLBACK_DELAY);
    }

    return toastId;
  };
};

// Export wrapped toast API
export const toast = {
  success: createToastWrapper(sonnerToast.success),
  error: createToastWrapper(sonnerToast.error),
  warning: createToastWrapper(sonnerToast.warning),
  info: createToastWrapper(sonnerToast.info),
  message: createToastWrapper(sonnerToast.message),
  promise: sonnerToast.promise,
  custom: sonnerToast.custom,
  loading: sonnerToast.loading,
  dismiss: sonnerToast.dismiss,
} as const;
